import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Megaphone, Plus, X, Copy, Check, Pencil, Upload } from 'lucide-react';
import styled from '@emotion/styled';
import {
  PageHeader, PageTitle, Card, CardGrid, Table, Thead, Tbody,
  PrimaryButton, SecondaryButton, GhostButton, Badge, Tab, TabBar,
  ModalOverlay, ModalDialog, ModalTitle, ModalActions,
  FormGroup, FormRow, Input, Textarea, Select, Mono,
} from '../styled-components';
import { EmptyState, SkeletonRows, StyledCheckboxBox, ResourceNotFound } from '../components';
import { useFetchData } from '../hooks';
import { showToast } from '../hooks';
import { fetchOrThrow, getCSRFToken, CSRF_HEADER_NAME } from '../../common/utils';
import { ConversationsList } from './conversations';

interface Campaign {
  id: string; name: string; status: string; campaign_code: string;
  total_contacts: number; total_calls: number; completed_calls: number;
  not_reached: number; pending: number;
  success_rate: number; created_at: string | null;
}

interface CampaignDetail extends Campaign {
  description: string;
  timezone: string;
  max_attempts: number;
  retry_delay_s: number;
  max_concurrency: number;
  origin_phone_numbers: string[];
  start_date: string;
  end_date: string | null;
  calling_windows: { day: number; start_time: string; end_time: string }[];
  enabled: boolean;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <button onClick={handleCopy} title="Copy" style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
      color: copied ? 'var(--accent-green)' : 'var(--text-muted)',
      transition: 'color 150ms', display: 'inline-flex', alignItems: 'center',
    }}>
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}

interface PhoneNumber { id: string; phone_number: string; label: string; status: string }
interface ComplianceForm { form_type: string; status: string }
type CallingWindow = { day: number; startTime: string; endTime: string };

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
];
const DEFAULT_WINDOWS: CallingWindow[] = [0, 1, 2, 3, 4].map(day => ({ day, startTime: '08:00', endTime: '21:00' }));

// The API takes retry_delay_s in seconds, with a one hour floor and a 30 day ceiling.
const RETRY_UNITS = { hours: 3600, days: 86400, weeks: 604800 } as const;
type RetryUnit = keyof typeof RETRY_UNITS;
const RETRY_UNIT_MAX: Record<RetryUnit, number> = { hours: 720, days: 30, weeks: 4 };

// Show a stored delay in the largest unit it divides evenly into, so a week reads as
// "1 week" rather than "168 hours".
function splitRetryDelay(seconds: number): { value: number; unit: RetryUnit } {
  const unit = (Object.keys(RETRY_UNITS) as RetryUnit[])
    .reverse()
    .find(u => seconds >= RETRY_UNITS[u] && seconds % RETRY_UNITS[u] === 0) ?? 'hours';
  return { value: Math.max(1, Math.round(seconds / RETRY_UNITS[unit])), unit };
}

const WindowRow = styled.div({
  display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8,
});
const PhoneList = styled.div({
  display: 'flex', flexDirection: 'column', gap: 8,
  maxHeight: 140, overflowY: 'auto',
  border: '1px solid var(--bg-border)', borderRadius: 'var(--radius-btn)',
  padding: '8px 12px', background: 'var(--bg-base)',
});
const PhoneLabel = styled.div({
  display: 'flex', alignItems: 'center', gap: 8,
  fontSize: 14, color: 'var(--text-primary)', cursor: 'pointer',
});

function CampaignModal({ onClose, onCreated, campaign }: { onClose: () => void; onCreated: () => void; campaign?: CampaignDetail }) {
  const isEdit = !!campaign;
  const navigate = useNavigate();
  const { data: phoneNumbers, loading: loadingNumbers } = useFetchData<PhoneNumber[]>('/dashboard/api/phone-numbers', []);
  const { data: complianceForms, loading: loadingCompliance } = useFetchData<ComplianceForm[]>('/dashboard/api/compliance/forms', []);
  const outboundApproved = complianceForms.some(f => f.form_type === 'outbound_dialing' && f.status === 'approved');

  const [name, setName] = useState(campaign?.name ?? '');
  const [description, setDescription] = useState(campaign?.description ?? '');
  const [selectedPhones, setSelectedPhones] = useState<string[]>(campaign?.origin_phone_numbers ?? []);
  const [startDate, setStartDate] = useState(campaign?.start_date ?? '');
  const [endDate, setEndDate] = useState(campaign?.end_date ?? '');
  const [tz, setTz] = useState(campaign?.timezone ?? 'America/Los_Angeles');
  const [maxAttempts, setMaxAttempts] = useState(campaign?.max_attempts ?? 1);
  const [retryAfter, setRetryAfter] = useState(() => splitRetryDelay(campaign?.retry_delay_s ?? 3600));
  const [maxConcurrency, setMaxConcurrency] = useState(campaign?.max_concurrency ?? 1);
  const [windows, setWindows] = useState<CallingWindow[]>(
    campaign?.calling_windows?.map(w => ({ day: w.day, startTime: w.start_time, endTime: w.end_time }))
    ?? DEFAULT_WINDOWS
  );
  const [submitting, setSubmitting] = useState(false);

  function togglePhone(number: string) {
    setSelectedPhones(prev => prev.includes(number) ? prev.filter(n => n !== number) : [...prev, number]);
  }

  function updateWindow(i: number, field: keyof CallingWindow, value: string | number) {
    setWindows(prev => prev.map((w, idx) => idx === i ? { ...w, [field]: value } : w));
  }

  async function handleSubmit() {
    // Compliance is checked first (outbound dialing must be approved)
    if (!isEdit && !loadingCompliance && !outboundApproved) {
      showToast('Outbound dialing is not enabled for this organization. Please go to https://app.goguava.ai/dashboard/compliance to get started.', 'error', 10000);
      return;
    }
    if (!isEdit && !name.trim()) { showToast('Campaign name is required', 'error'); return; }
    if (selectedPhones.length === 0) { showToast('Select at least one phone number', 'error'); return; }
    if (!startDate) { showToast('Start date is required', 'error'); return; }
    if (windows.length === 0) { showToast('Add at least one calling window', 'error'); return; }

    setSubmitting(true);
    const payload = {
      description: description.trim() || null,
      origin_phone_numbers: selectedPhones,
      start_date: startDate,
      end_date: endDate || null,
      timezone: tz,
      max_attempts: maxAttempts,
      retry_delay_s: retryAfter.value * RETRY_UNITS[retryAfter.unit],
      max_concurrency: maxConcurrency,
      calling_windows: windows.map(w => ({ day: w.day, start_time: w.startTime, end_time: w.endTime })),
    };
    try {
      if (isEdit) {
        // Send only the fields the user actually changed. A schedule field in the body
        // makes the backend re-spread every contact's next-call time, which pauses
        // dialing for minutes — so an untouched field must not appear here.
        // Stable key order so object values compare by content, not serialisation.
        const canon = (v: unknown) => JSON.stringify(v, (_key, val) =>
          val && typeof val === 'object' && !Array.isArray(val)
            ? Object.fromEntries(Object.entries(val).sort(([a], [b]) => a.localeCompare(b)))
            : val);
        const current = campaign as unknown as Record<string, unknown>;
        const changed = Object.fromEntries(
          Object.entries(payload).filter(([key, value]) => canon(value) !== canon(current[key] ?? null)),
        );
        if (Object.keys(changed).length === 0) {
          showToast('No changes to save', 'success');
          onClose();
          return;
        }
        await fetchOrThrow(`/dashboard/api/campaigns/${campaign.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: getCSRFToken() },
          body: JSON.stringify(changed),
        });
        showToast('Campaign updated', 'success');
        onCreated();
        onClose();
      } else {
        const res = await fetchOrThrow('/dashboard/api/campaigns', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: getCSRFToken() },
          body: JSON.stringify({ ...payload, name: name.trim() }),
        });
        const { id } = await res.json();
        showToast('Campaign created — now add contacts', 'success');
        onClose();
        navigate(`/campaigns/${id}/upload`);
      }
    } catch (e) {
      let msg = isEdit ? 'Failed to update campaign' : 'Failed to create campaign';
      if (e instanceof Error) {
        const match = e.message.match(/— (.+)$/s);
        if (match) { try { msg = JSON.parse(match[1]).error ?? msg; } catch {} }
      }
      showToast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalDialog
        style={{ width: 560, maxWidth: 'calc(100vw - 48px)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        <ModalTitle>{isEdit ? 'Edit Campaign' : 'New Campaign'}</ModalTitle>

        <div style={{ overflowY: 'auto', flex: 1, paddingRight: 2 }}>
          <FormGroup>
            <label>Name</label>
            <Input placeholder="Q4 Outreach" value={name} onChange={e => setName(e.target.value)} disabled={submitting || isEdit} />
          </FormGroup>

          <FormGroup>
            <label>Description <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
            <Textarea
              placeholder="Brief description of campaign goals…"
              value={description}
              onChange={e => setDescription(e.target.value)}
              disabled={submitting}
              style={{ minHeight: 60 }}
            />
          </FormGroup>

          <FormRow>
            <FormGroup style={{ flex: 1 }}>
              <label>Start Date</label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} disabled={submitting} />
            </FormGroup>
            <FormGroup style={{ flex: 1 }}>
              <label>End Date <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} disabled={submitting} />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup style={{ flex: 1.7 }}>
              <label>Timezone</label>
              <Select value={tz} onChange={e => setTz(e.target.value)} disabled={submitting}>
                {TIMEZONES.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            </FormGroup>
            <FormGroup style={{ flex: 1.1 }}>
              <label>Max Concurrency</label>
              <Input
                type="number" min={1} value={maxConcurrency}
                onChange={e => setMaxConcurrency(Math.max(1, parseInt(e.target.value, 10) || 1))}
                disabled={submitting}
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup style={{ flex: 1 }}>
              <label>Max Attempts</label>
              <Input
                type="number" min={1} value={maxAttempts}
                onChange={e => setMaxAttempts(Math.max(1, parseInt(e.target.value, 10) || 1))}
                disabled={submitting}
              />
            </FormGroup>
            <FormGroup style={{ flex: 1.8 }}>
              <label>
                Retry After
                {maxAttempts <= 1 && <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none' }}> — needs more than one attempt</span>}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <Input
                  type="number" min={1} max={RETRY_UNIT_MAX[retryAfter.unit]} value={retryAfter.value}
                  onChange={e => setRetryAfter(prev => ({
                    ...prev,
                    value: Math.min(RETRY_UNIT_MAX[prev.unit], Math.max(1, parseInt(e.target.value, 10) || 1)),
                  }))}
                  disabled={submitting || maxAttempts <= 1}
                  style={{ flex: 1 }}
                />
                <Select
                  value={retryAfter.unit}
                  onChange={e => setRetryAfter(prev => {
                    const unit = e.target.value as RetryUnit;
                    return { unit, value: Math.min(RETRY_UNIT_MAX[unit], prev.value) };
                  })}
                  disabled={submitting || maxAttempts <= 1}
                  style={{ flex: 1 }}
                >
                  {(Object.keys(RETRY_UNITS) as RetryUnit[]).map(u => <option key={u} value={u}>{u}</option>)}
                </Select>
              </div>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <label>Phone Numbers</label>
            {loadingNumbers ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
            ) : phoneNumbers.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No phone numbers found. Add one in the Phone Numbers section.</div>
            ) : (
              <PhoneList>
                {phoneNumbers.map(n => (
                  <PhoneLabel
                    key={n.id}
                    onClick={submitting ? undefined : () => togglePhone(n.phone_number)}
                  >
                    <StyledCheckboxBox checked={selectedPhones.includes(n.phone_number)} disabled={submitting} />
                    {n.label ? `${n.phone_number} (${n.label})` : n.phone_number}
                  </PhoneLabel>
                ))}
              </PhoneList>
            )}
          </FormGroup>

          <FormGroup>
            <label>Calling Windows</label>
            {windows.map((w, i) => (
              <WindowRow key={`${w.day}-${w.startTime}`}>
                <Select
                  value={w.day}
                  onChange={e => updateWindow(i, 'day', parseInt(e.target.value, 10))}
                  disabled={submitting}
                  style={{ width: 130, flexShrink: 0 }}
                >
                  {DAYS_OF_WEEK.map((d, idx) => <option key={d} value={idx}>{d}</option>)}
                </Select>
                <Input type="time" value={w.startTime} onChange={e => updateWindow(i, 'startTime', e.target.value)} disabled={submitting} />
                <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>–</span>
                <Input type="time" value={w.endTime} onChange={e => updateWindow(i, 'endTime', e.target.value)} disabled={submitting} />
                <GhostButton type="button" onClick={() => setWindows(prev => prev.filter((_, idx) => idx !== i))} disabled={submitting} style={{ flexShrink: 0 }}>
                  <X size={14} />
                </GhostButton>
              </WindowRow>
            ))}
            <SecondaryButton
              type="button"
              onClick={() => setWindows(prev => [...prev, { day: 0, startTime: '08:00', endTime: '21:00' }])}
              disabled={submitting}
              style={{ fontSize: 13, padding: '5px 12px' }}
            >
              <Plus size={13} /> Add window
            </SecondaryButton>
          </FormGroup>
        </div>

        <ModalActions style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--bg-border)' }}>
          <SecondaryButton onClick={onClose} disabled={submitting}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSubmit} disabled={submitting}>
            {submitting ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Create Campaign')}
          </PrimaryButton>
        </ModalActions>
      </ModalDialog>
    </ModalOverlay>
  );
}

function formatDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Status toggle
// ---------------------------------------------------------------------------

function StatusToggle({ campaign, onToggled }: { campaign: Campaign; onToggled: () => void }) {
  const [loading, setLoading] = useState(false);

  async function toggle() {
    setLoading(true);
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    try {
      await fetchOrThrow(`/dashboard/api/campaigns/${campaign.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify({ status: newStatus }),
      });
      showToast(`Campaign ${newStatus}`, 'success');
      onToggled();
    } catch {
      showToast('Failed to update status', 'error');
    } finally { setLoading(false); }
  }

  if (campaign.status !== 'active' && campaign.status !== 'paused') return null;

  return (
    <SecondaryButton style={{ padding: '5px 12px', fontSize: 13 }} onClick={toggle} disabled={loading}>
      {loading ? '…' : campaign.status === 'active' ? 'Pause' : 'Resume'}
    </SecondaryButton>
  );
}

// ---------------------------------------------------------------------------
// Campaigns list
// ---------------------------------------------------------------------------

export function CampaignsPage() {
  const navigate = useNavigate();
  const { data, loading, refetch } = useFetchData<Campaign[]>('/dashboard/api/campaigns', []);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div>
      {showCreate && <CampaignModal onClose={() => setShowCreate(false)} onCreated={refetch} />}
      <PageHeader>
        <PageTitle>Campaigns</PageTitle>
        <PrimaryButton onClick={() => setShowCreate(true)}><Plus size={14} /> New Campaign</PrimaryButton>
      </PageHeader>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <Table>
          <Thead>
            <tr>
              <th>Name</th><th>Code</th><th>Status</th><th>Contacts</th><th>Tried</th><th>Reached</th><th>Pickup Rate</th><th>Pending</th><th>Created</th><th></th>
            </tr>
          </Thead>
          <Tbody>
            {loading ? (
              <SkeletonRows cols={10} />
            ) : data.length === 0 ? (
              <tr><td colSpan={10}>
                <EmptyState
                  icon={<Megaphone size={40} />}
                  title="No campaigns yet"
                  body="Create a new campaign to get started."
                />
              </td></tr>
            ) : data.map((c) => (
              <tr key={c.id} onClick={() => navigate(`/campaigns/${c.id}`)}>
                <td style={{ fontWeight: 500 }}>{c.name}</td>
                <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                  <Mono style={{ fontSize: 12 }}>{c.campaign_code}</Mono>
                  <CopyButton value={c.campaign_code} />
                </td>
                <td><Badge variant={c.status}>{c.status}</Badge></td>
                <td>{c.total_contacts.toLocaleString()}</td>
                <td>{(c.completed_calls + c.not_reached).toLocaleString()}</td>
                <td>{c.completed_calls.toLocaleString()}</td>
                <td>{c.success_rate}%</td>
                <td>{c.pending.toLocaleString()}</td>
                <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{formatDate(c.created_at)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <StatusToggle campaign={c} onToggled={refetch} />
                </td>
              </tr>
            ))}
          </Tbody>
        </Table>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campaign detail
// ---------------------------------------------------------------------------

const StatCard = styled(Card)({
  textAlign: 'center',
  '& .stat-value': { fontSize: 32, fontWeight: 700, color: 'var(--text-primary)' },
  '& .stat-label': { fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 },
});

export function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { data: campaign, loading, refetch } = useFetchData<CampaignDetail | null>(
    `/dashboard/api/campaigns/${campaignId}`, null
  );
  const [activeTab, setActiveTab] = useState<'overview' | 'conversations'>('overview');
  const [showEdit, setShowEdit] = useState(false);

  if (loading) return <div style={{ color: 'var(--text-secondary)', padding: 32 }}>Loading…</div>;
  if (!campaign) return <ResourceNotFound label="Campaign" backTo="/campaigns" />;

  return (
    <div>
      {showEdit && (
        <CampaignModal campaign={campaign} onClose={() => setShowEdit(false)} onCreated={() => { setShowEdit(false); refetch(); }} />
      )}
      <PageHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>←</button>
          <PageTitle>{campaign.name}</PageTitle>
          <Badge variant={campaign.status}>{campaign.status}</Badge>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <SecondaryButton style={{ padding: '5px 12px', fontSize: 13 }} onClick={() => setShowEdit(true)}>
            <Pencil size={13} /> Edit
          </SecondaryButton>
          <SecondaryButton style={{ padding: '5px 12px', fontSize: 13 }} onClick={() => navigate(`/campaigns/${campaignId}/upload`)}>
            <Upload size={13} /> Add contacts
          </SecondaryButton>
          <StatusToggle campaign={campaign} onToggled={refetch} />
        </div>
      </PageHeader>

      <TabBar>
        <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>Overview</Tab>
        <Tab active={activeTab === 'conversations'} onClick={() => setActiveTab('conversations')}>Conversations</Tab>
      </TabBar>

      {activeTab === 'overview' && (
        <CardGrid cols={5}>
          <StatCard>
            <div className="stat-value">{campaign.total_contacts.toLocaleString()}</div>
            <div className="stat-label">Total Contacts</div>
          </StatCard>
          <StatCard>
            <div className="stat-value">{(campaign.completed_calls + campaign.not_reached).toLocaleString()}</div>
            <div className="stat-label">Tried</div>
          </StatCard>
          <StatCard>
            <div className="stat-value">{campaign.completed_calls.toLocaleString()}</div>
            <div className="stat-label">Reached</div>
          </StatCard>
          <StatCard>
            <div className="stat-value">{campaign.success_rate}%</div>
            <div className="stat-label">Pickup Rate</div>
          </StatCard>
          <StatCard>
            <div className="stat-value">{campaign.pending.toLocaleString()}</div>
            <div className="stat-label">Pending</div>
          </StatCard>
        </CardGrid>
      )}

      {activeTab === 'conversations' && (
        <ConversationsList campaignId={campaignId} />
      )}
    </div>
  );
}
